import { isObject } from 'src/utils/typeguards/isObject';
import { createDatadogError } from 'src/services/datadog/utils/createDatadogError';
import { FetchClientDomain } from './domain';

const FetchRequestBlockedError = createDatadogError('FetchRequestBlocked');

const AbortError = createDatadogError('AbortError');
export abstract class FetchClient implements FetchClientDomain.FetchClient {
    public static readonly AbortError = AbortError;
    public static readonly FetchRequestBlockedError = FetchRequestBlockedError;

    async fetch(url: RequestInfo, opts: FetchClientDomain.TimeoutableClientFetchConfig = {}): Promise<Response> {
        const { query } = opts;
        const urlWithQueryString = `${url}${this.#generateQueryString(query)}`;

        const finalOpts = this.#prepareFinalFetchOptions(opts);

        return await this.#fetch(urlWithQueryString, finalOpts);
    }

    /**
     * Should proxy calling the environment specific fetch, and handle any errors that might be thrown.
     * Abstracts away the need to know whether we're running in a browser or in node in this class
     */
    protected abstract performFetch(url: RequestInfo | URL, opts: RequestInit): Promise<Response>;

    async #fetch(url: RequestInfo | URL, opts: FetchClientDomain.TimeoutableClientFetchConfig): Promise<Response> {
        if (opts.timeout) {
            return await this.#timeoutableFetch(
                url,
                opts as FetchClientDomain.TimeoutableClientFetchConfig,
                opts.timeout
            );
        }
        return await this.performFetch(url, opts as RequestInit);
    }

    async #timeoutableFetch(
        url: RequestInfo | URL,
        opts?: FetchClientDomain.TimeoutableClientFetchConfig,
        timeout?: number
    ): Promise<Response> {
        const controller = new AbortController();
        // Testing the abort signal is onerous, and all we'd be testing is that the setTimeout and abort APIs work as intended, which is native JS
        // istanbul ignore next
        const id = setTimeout(() => controller.abort(), timeout);
        const init = {
            ...opts,
            signal: controller.signal,
        } as RequestInit;
        const response = await this.performFetch(url, init);
        clearTimeout(id);
        return response as Response;
    }

    #prepareFinalFetchOptions = (
        opts: FetchClientDomain.TimeoutableClientFetchConfig
    ): FetchClientDomain.TimeoutableClientFetchConfig => {
        const { query: _dontIncludeQueryInOpts, body, ...rest } = opts;

        if (body) {
            return { ...rest, body: this.#prepareBody(body) } as FetchClientDomain.TimeoutableClientFetchConfig;
        }
        return rest;
    };

    #prepareBody = (body: unknown) => {
        if (isObject(body) && !(body instanceof FormData)) {
            return JSON.stringify(body);
        }
        return body;
    };

    #generateQueryString = (query: Record<string, string> | string | undefined) => {
        if (!query) {
            return '';
        }
        if (typeof query === 'string') {
            return `?${query}`;
        }
        return `?${new URLSearchParams(query)}`;
    };
}
